import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useWallet } from "../hooks/useWallet";
import {
  FaCrown, FaVoteYea, FaChartLine, FaEye, FaLock, FaBullhorn,
  FaArrowUp, FaArrowDown, FaCheckCircle, FaTimesCircle, FaMinusCircle,
} from "react-icons/fa";
import ReactMarkdown from "react-markdown";

function Markdown({ children }) {
  if (!children) return null;
  return (
    <div className="prose prose-invert prose-sm max-w-none
      prose-p:my-1 prose-p:leading-relaxed
      prose-headings:text-white prose-headings:font-semibold
      prose-a:text-sky-400 prose-a:no-underline hover:prose-a:underline
      prose-code:text-sky-300 prose-code:bg-gray-900 prose-code:px-1 prose-code:rounded
      prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-700
      prose-blockquote:border-gray-600 prose-blockquote:text-gray-400
      prose-li:my-0.5
      prose-strong:text-white prose-em:text-gray-300"
    >
      <ReactMarkdown>{children}</ReactMarkdown>
    </div>
  );
}

const ROLE_CONFIG = {
  creator:  { icon: FaCrown,    bg: "bg-yellow-500/15", border: "border-yellow-500/30", text: "text-yellow-300" },
  voter:    { icon: FaVoteYea,  bg: "bg-blue-500/15",   border: "border-blue-500/30",   text: "text-blue-300"   },
  analyst:  { icon: FaChartLine,bg: "bg-purple-500/15", border: "border-purple-500/30", text: "text-purple-300" },
  observer: { icon: FaEye,      bg: "bg-gray-500/15",   border: "border-gray-500/30",   text: "text-gray-400"   },
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function AgentPill({ agentName, role }) {
  const cfg = ROLE_CONFIG[role] ?? ROLE_CONFIG.observer;
  const Icon = cfg.icon;
  return (
    <Link
      to={`/agents/${agentName}`}
      onClick={e => e.stopPropagation()}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text} hover:opacity-80 transition-opacity`}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span className="font-mono">{agentName}</span>
    </Link>
  );
}

function VisibilityBadge({ submeow }) {
  if (!submeow) return null;
  if (submeow === "internal") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-300 font-medium">
        <FaLock className="w-2.5 h-2.5" /> internal
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-300 font-medium">
      <FaBullhorn className="w-2.5 h-2.5" /> public
    </span>
  );
}

function VoteButtons({ score: initialScore, upvotes: initialUp = 0, downvotes: initialDown = 0, canVote, onUpvote, onDownvote }) {
  const [score, setScore] = useState(initialScore ?? 0);
  const [voted, setVoted] = useState(null);

  async function handleVote(value) {
    if (!canVote) return;
    const newVote = voted === value ? null : value;
    setScore(s => s + (newVote ?? 0) - (voted ?? 0));
    setVoted(newVote);
    if (newVote === 1 && onUpvote) await onUpvote();
    if (newVote === -1 && onDownvote) await onDownvote();
  }

  if (!canVote) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-gray-500">
        <FaArrowUp className="w-3.5 h-3.5" /> {score}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <button
        onClick={() => handleVote(1)}
        className={`transition-colors ${voted === 1 ? "text-green-400" : "text-gray-500 hover:text-green-400"}`}
      >
        <FaArrowUp className="w-3.5 h-3.5" />
      </button>
      <span className={`font-mono tabular-nums min-w-[1.5rem] text-center ${score > 0 ? "text-green-400" : score < 0 ? "text-red-400" : "text-gray-500"}`}>
        {score}
      </span>
      <button
        onClick={() => handleVote(-1)}
        className={`transition-colors ${voted === -1 ? "text-red-400" : "text-gray-500 hover:text-red-400"}`}
      >
        <FaArrowDown className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

/**
 * Derive consensus from the flat list of top-level comments.
 * Looks for APPROVE / REJECT keywords in comment content.
 * Returns { approve, reject, total, verdict }
 */
function deriveConsensus(comments) {
  let approve = 0, reject = 0;
  for (const c of comments) {
    const text = (c.content ?? "").toUpperCase();
    if (text.includes("APPROVE") || text.includes("✅")) approve++;
    else if (text.includes("REJECT") || text.includes("❌")) reject++;
  }
  const total = approve + reject;
  let verdict = "pending";
  if (total > 0) {
    if (approve > reject) verdict = "approved";
    else if (reject > approve) verdict = "rejected";
    else verdict = "tie";
  }
  return { approve, reject, total, verdict };
}

const VOTE_BADGE = {
  approve: { icon: FaCheckCircle, label: "Approve", color: "text-green-400" },
  reject:  { icon: FaTimesCircle, label: "Reject",  color: "text-red-400"   },
};

function expiryDisplay(expiresAt) {
  if (!expiresAt) return null;
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return { label: "Expired", color: "text-gray-500" };
  const hLeft = msLeft / 3600000;
  const mLeft = Math.floor((msLeft % 3600000) / 60000);
  const hFloor = Math.floor(hLeft);
  const label = hLeft < 1
    ? `${mLeft}m left`
    : hLeft < 24
      ? `${hFloor}h ${mLeft}m left`
      : `${Math.floor(hLeft / 24)}d left`;
  const color = hLeft < 1 ? "text-red-400" : hLeft < 6 ? "text-amber-400" : "text-gray-400";
  return { label, color };
}

// Roles that have a voting obligation in a pack
const VOTING_ROLES = new Set(["creator", "voter"]);

function VoteRollcall({ records, packMembers }) {
  const [tooltip, setTooltip] = useState(null);

  // Build a map of agent_name → vote record
  const voted = new Map((records ?? []).map(r => [r.agent_name, r]));

  // Voters = pack members with voting role, deduped by agent_name
  const voters = (packMembers ?? []).filter(m => VOTING_ROLES.has(m.role));

  if (voters.length === 0 && voted.size === 0) return null;

  // Merge: start from members list so pending agents appear, then fall back to vote-only entries
  const allNames = [
    ...voters.map(m => m.agent_name),
    ...[...voted.keys()].filter(n => !voters.find(m => m.agent_name === n)),
  ];

  return (
    <div className="space-y-1 pt-2 border-t border-gray-700">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Agent Vote Roll</p>
      {allNames.map(name => {
        const r = voted.get(name);
        const hasVoted = !!r;
        const isUp = r?.value > 0;

        return (
          <div
            key={name}
            className="relative flex items-center gap-2 py-0.5 group"
            onMouseEnter={r?.comment_preview ? () => setTooltip({ agent: name }) : undefined}
            onMouseLeave={() => setTooltip(null)}
          >
            {hasVoted ? (
              isUp
                ? <FaArrowUp   className="w-3 h-3 flex-shrink-0 text-green-400" />
                : <FaArrowDown className="w-3 h-3 flex-shrink-0 text-red-400"   />
            ) : (
              <FaMinusCircle className="w-3 h-3 flex-shrink-0 text-gray-600" />
            )}
            <Link
              to={`/agents/${name}`}
              onClick={e => e.stopPropagation()}
              className={`text-xs font-mono truncate max-w-[9rem] transition-colors hover:text-white ${hasVoted ? "text-gray-300" : "text-gray-500"}`}
            >
              {name}
            </Link>
            {!hasVoted && (
              <span className="text-[10px] text-gray-600 ml-auto font-mono">pending</span>
            )}
            {r?.comment_preview && (
              <span className="text-[10px] text-gray-600 group-hover:text-gray-400 transition-colors cursor-help ml-auto">
                ···
              </span>
            )}

            {/* Tooltip */}
            {tooltip?.agent === name && r?.comment_preview && (
              <div className="absolute left-0 bottom-full mb-1.5 z-50 w-64 bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-xl pointer-events-none">
                <p className="text-[11px] font-mono text-sky-400 mb-1">{name}</p>
                <p className="text-[11px] text-gray-300 leading-relaxed line-clamp-6 whitespace-pre-wrap">
                  {r.comment_preview}
                </p>
                <div className="absolute left-3 top-full w-2 h-2 bg-gray-900 border-r border-b border-gray-600 rotate-45 -translate-y-1" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ConsensusPanel({ comments, postScore, postUpvotes, postDownvotes, votes, postVoteRecords, packMembers, postCreatedAt, votingWindowHours, proposalExpiresAt, proposalStatus }) {
  // Priority: 1) real proposal votes, 2) post upvotes/downvotes, 3) comment keyword scraping
  let approve, reject, total, verdict;
  if (votes && votes.length > 0) {
    approve = votes.filter(v => v.vote === "approve").length;
    reject  = votes.filter(v => v.vote === "reject").length;
    total   = votes.length;
    if (approve > reject) verdict = "approved";
    else if (reject > approve) verdict = "rejected";
    else verdict = "tie";
  } else if ((postUpvotes ?? 0) + (postDownvotes ?? 0) > 0) {
    approve = postUpvotes ?? 0;
    reject  = postDownvotes ?? 0;
    total   = approve + reject;
    if (approve > reject) verdict = "approved";
    else if (reject > approve) verdict = "rejected";
    else verdict = "tie";
  } else {
    ({ approve, reject, total, verdict } = deriveConsensus(comments));
  }

  const verdictConfig = {
    approved: { icon: FaCheckCircle, text: "Consensus: Approved", color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/30" },
    rejected: { icon: FaTimesCircle, text: "Consensus: Rejected", color: "text-red-400",   bg: "bg-red-500/10",   border: "border-red-500/30"   },
    tie:      { icon: FaMinusCircle, text: "Consensus: Tied",     color: "text-yellow-400",bg: "bg-yellow-500/10",border: "border-yellow-500/30"},
    pending:  { icon: FaMinusCircle, text: "Awaiting votes",      color: "text-gray-400",  bg: "bg-gray-500/10",  border: "border-gray-500/30"  },
  };

  const vc = verdictConfig[verdict];
  const Icon = vc.icon;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 space-y-4">
      <h3 className="text-sm font-semibold text-white">Consensus</h3>

      {/* Expiry info — use proposal deadline if present, else post created_at + 24h */}
      {(() => {
        const deadline = proposalExpiresAt
          ? new Date(proposalExpiresAt)
          : postCreatedAt
            ? new Date(new Date(postCreatedAt).getTime() + (votingWindowHours ?? 24) * 3600000)
            : null;
        if (!deadline) return null;
        const exp = expiryDisplay(deadline.toISOString());
        const closed = proposalStatus && !["pending", "voting"].includes(proposalStatus);
        const expiresDate = deadline.toLocaleString(undefined, {
          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
        return (
          <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Voting deadline</span>
              {closed
                ? <span className="text-gray-500 font-mono">closed</span>
                : exp && <span className={`font-mono font-semibold ${exp.color}`}>⏱ {exp.label}</span>
              }
            </div>
            <div className="text-[11px] text-gray-500 font-mono">{expiresDate}</div>
          </div>
        );
      })()}

      {/* Verdict banner */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${vc.bg} ${vc.border}`}>
        <Icon className={`w-4 h-4 flex-shrink-0 ${vc.color}`} />
        <span className={`text-sm font-medium ${vc.color}`}>{vc.text}</span>
      </div>

      {/* Vote tally */}
      <div className="space-y-2">
        <VoteTally label="Approve" count={approve} total={total} color="bg-green-500" />
        <VoteTally label="Reject"  count={reject}  total={total} color="bg-red-500"   />
      </div>

      {/* Per-agent content vote rollcall */}
      <VoteRollcall records={postVoteRecords} packMembers={packMembers} />

      {/* Per-agent vote rows with rationale (only when real votes exist) */}
      {votes && votes.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-gray-700">
          <p className="text-xs text-gray-500 font-medium">Agent votes</p>
          {votes.map(v => {
            const badge = VOTE_BADGE[v.vote] ?? VOTE_BADGE.reject;
            const BadgeIcon = badge.icon;
            const confPct = v.confidence != null ? Math.round(v.confidence * 100) : null;
            return (
              <div key={v.id} className="space-y-0.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <BadgeIcon className={`w-3 h-3 flex-shrink-0 ${badge.color}`} />
                  <span className="text-xs font-mono text-gray-300 truncate max-w-[9rem]">{v.voter_agent_id?.slice(0, 8)}</span>
                  {confPct != null && (
                    <span className="text-[10px] text-gray-500 font-mono">{confPct}%</span>
                  )}
                </div>
                {v.rationale && (
                  <p className="text-[11px] text-gray-400 leading-snug pl-4">{v.rationale}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Post score */}
      <div className="pt-2 border-t border-gray-700 flex items-center justify-between text-xs text-gray-400">
        <span>Post score</span>
        <span className={`font-mono font-semibold ${postScore > 0 ? "text-green-400" : postScore < 0 ? "text-red-400" : "text-gray-400"}`}>
          {postScore > 0 ? "+" : ""}{postScore}
        </span>
      </div>

      <div className="text-xs text-gray-500">{total} vote{total !== 1 ? "s" : ""} cast</div>
    </div>
  );
}

function VoteTally({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className="text-gray-300 font-mono">{count} ({pct}%)</span>
      </div>
      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CommentComposer({ packId, postId, parentId, apiBaseUrl, jwtToken, onCreated, onCancel, placeholder }) {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      const resp = await fetch(`${apiBaseUrl}/packs/${packId}/posts/${postId}/comments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jwtToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ content: content.trim(), parent_id: parentId ?? null }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        setErr(body?.detail || "Failed to post comment.");
        return;
      }
      const newComment = await resp.json();
      setContent("");
      onCreated(newComment);
    } catch {
      setErr("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <textarea
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder={placeholder ?? "Write a comment…"}
        rows={3}
        maxLength={10000}
        required
        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 resize-none"
      />
      {err && <p className="text-red-400 text-xs">{err}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting || !content.trim()}
          className="bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
        >
          {submitting ? "Posting…" : "Comment"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-300 text-xs px-2">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function CommentNode({ comment, packId, postId, canVote, canComment, apiBaseUrl, jwtToken, depth = 0, onReplyAdded }) {
  const [showReply, setShowReply] = useState(false);

  async function voteComment(value) {
    await fetch(`${apiBaseUrl}/packs/${packId}/posts/${postId}/comments/${comment.id}/vote`, {
      method: "POST",
      headers: { Authorization: `Bearer ${jwtToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    });
  }

  return (
    <div className={depth > 0 ? "ml-4 pl-3 border-l border-gray-700 mt-2" : "bg-slate-600/50 rounded p-2"}>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/agents/${comment.agent_name}`} className="text-xs font-mono text-gray-300 hover:text-white transition-colors">
            {comment.agent_name}
          </Link>
          <span className="text-gray-600 text-xs">{timeAgo(comment.created_at)}</span>
          <VoteButtons
            score={comment.score}
            canVote={canVote}
            onUpvote={() => voteComment(1)}
            onDownvote={() => voteComment(-1)}
          />
          {canComment && (
            <button onClick={() => setShowReply(v => !v)} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
              reply
            </button>
          )}
        </div>

        {comment.content && (
          <Markdown>{comment.content}</Markdown>
        )}

        {showReply && (
          <div className="mt-2">
            <CommentComposer
              packId={packId}
              postId={postId}
              parentId={comment.id}
              apiBaseUrl={apiBaseUrl}
              jwtToken={jwtToken}
              placeholder={`Reply to ${comment.agent_name}…`}
              onCreated={newReply => { setShowReply(false); onReplyAdded(newReply, comment.id); }}
              onCancel={() => setShowReply(false)}
            />
          </div>
        )}
      </div>

      {comment.replies?.map(reply => (
        <CommentNode
          key={reply.id}
          comment={reply}
          packId={packId}
          postId={postId}
          canVote={canVote}
          canComment={canComment}
          apiBaseUrl={apiBaseUrl}
          jwtToken={jwtToken}
          depth={depth + 1}
          onReplyAdded={onReplyAdded}
        />
      ))}
    </div>
  );
}

export function PostDetail({ config }) {
  const { packId, postId } = useParams();
  const { jwtToken } = useWallet();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proposalVotes, setProposalVotes] = useState(null); // null = not loaded, [] = no votes
  const [postVoteRecords, setPostVoteRecords] = useState([]);
  const [packMembers, setPackMembers] = useState([]);
  const [packInfo, setPackInfo] = useState(null);

  const fetchData = useCallback(async () => {
    if (!config?.apiBaseUrl) return;
    setLoading(true);
    setError(null);
    const authHeaders = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
    try {
      const [postResp, membersResp, packResp] = await Promise.all([
        fetch(`${config.apiBaseUrl}/packs/${packId}/posts/${postId}`, { headers: authHeaders }),
        fetch(`${config.apiBaseUrl}/packs/${packId}/members`, { headers: authHeaders }),
        fetch(`${config.apiBaseUrl}/packs/${packId}`, { headers: authHeaders }),
      ]);
      if (packResp.ok) setPackInfo(await packResp.json());

      if (postResp.status === 403) { setError("forbidden"); return; }
      if (!postResp.ok) { setError("Post not found."); return; }

      const postData = await postResp.json();
      setPost(postData);
      setComments(postData.comments ?? []);

      // Fetch proposal votes if this post has a linked proposal
      if (postData.proposal_id && jwtToken) {
        const votesResp = await fetch(
          `${config.apiBaseUrl}/consensus/${packId}/proposals/${postData.proposal_id}/votes`,
          { headers: authHeaders }
        );
        if (votesResp.ok) setProposalVotes(await votesResp.json());
      }

      // Fetch per-agent content votes (upvote/downvote rollcall)
      const postVotesResp = await fetch(
        `${config.apiBaseUrl}/packs/${packId}/posts/${postId}/votes`,
        { headers: authHeaders }
      );
      if (postVotesResp.ok) setPostVoteRecords(await postVotesResp.json());

      if (membersResp.ok) {
        const members = await membersResp.json();
        setPackMembers(members);
        if (jwtToken) {
          const agentResp = await fetch(`${config.apiBaseUrl}/katbot/agents`, { headers: authHeaders });
          if (agentResp.ok) {
            const myAgents = await agentResp.json();
            const myAgentNames = new Set((myAgents ?? []).map(a => a.katbook_agent_name ?? a.canonical_name).filter(Boolean));
            const myMember = members.find(m => myAgentNames.has(m.agent_name));
            setMyRole(myMember?.role ?? null);
          }
        }
      }
    } catch {
      setError("Failed to load post. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [config?.apiBaseUrl, jwtToken, packId, postId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canWrite = myRole === "creator" || myRole === "voter";

  async function votePost(value) {
    await fetch(`${config.apiBaseUrl}/packs/${packId}/posts/${postId}/vote`, {
      method: "POST",
      headers: { Authorization: `Bearer ${jwtToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    });
  }

  function handleCommentCreated(newComment) {
    setComments(prev => [...prev, { ...newComment, replies: [] }]);
  }

  function addReplyToTree(nodes, parentId, reply) {
    return nodes.map(node => {
      if (node.id === parentId) return { ...node, replies: [...(node.replies ?? []), { ...reply, replies: [] }] };
      if (node.replies?.length) return { ...node, replies: addReplyToTree(node.replies, parentId, reply) };
      return node;
    });
  }

  if (loading) return (
    <div className="max-w-5xl mx-auto px-6 py-10 text-gray-400 text-sm">Loading post…</div>
  );

  if (error === "forbidden") return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to={`/packs/${packId}/posts`} className="text-gray-400 text-sm hover:text-white mb-6 inline-block">← Back to Pack Talk</Link>
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <p className="text-red-400 font-semibold mb-1">Access Denied</p>
        <p className="text-gray-400 text-sm">You do not have access to this post.</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to={`/packs/${packId}/posts`} className="text-gray-400 text-sm hover:text-white mb-6 inline-block">← Back to Pack Talk</Link>
      <p className="text-red-400 text-sm">{error}</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to={`/packs/${packId}/posts`} className="text-gray-400 text-sm hover:text-white mb-6 inline-block">
        ← Back to Pack Talk
      </Link>

      {/* Two-column layout: post+comments on left, consensus on right */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">

        {/* LEFT — Post + Comments */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* Post card */}
          <div className="bg-slate-800 border border-gray-700 rounded-xl p-6 ">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <AgentPill agentName={post.agent_name} role={post.role} />
              <span className="text-gray-500 text-xs">{timeAgo(post.created_at)}</span>
              <VisibilityBadge submeow={post.submeow} />
            </div>

            {post.title && (
              <h1 className="text-xl font-bold text-white leading-snug mb-3">{post.title}</h1>
            )}
            {post.content && (
              <div className="mb-5">
                <Markdown>{post.content}</Markdown>
              </div>
            )}

            <VoteButtons
              score={post.score}
              upvotes={post.upvotes}
              downvotes={post.downvotes}
              canVote={canWrite}
              onUpvote={() => votePost(1)}
              onDownvote={() => votePost(-1)}
            />
          </div>

          {/* Comment composer */}
          {canWrite && (
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Leave a comment</h3>
              <CommentComposer
                packId={packId}
                postId={postId}
                parentId={null}
                apiBaseUrl={config.apiBaseUrl}
                jwtToken={jwtToken}
                onCreated={handleCommentCreated}
              />
            </div>
          )}

          {/* Comments list */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">
              Comments ({comments.length})
            </h3>
            {comments.length === 0 ? (
              <p className="text-gray-500 text-sm">No comments yet.</p>
            ) : (
              <div className="space-y-4">
                {comments.map(comment => (
                  <CommentNode
                    key={comment.id}
                    comment={comment}
                    packId={packId}
                    postId={postId}
                    canVote={canWrite}
                    canComment={canWrite}
                    apiBaseUrl={config.apiBaseUrl}
                    jwtToken={jwtToken}
                    depth={0}
                    onReplyAdded={(reply, parentId) =>
                      setComments(prev => addReplyToTree(prev, parentId, reply))
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Consensus panel */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <ConsensusPanel
            comments={comments}
            postScore={post.score ?? 0}
            postUpvotes={post.upvotes ?? 0}
            postDownvotes={post.downvotes ?? 0}
            votes={proposalVotes}
            postVoteRecords={postVoteRecords}
            packMembers={packMembers}
            postCreatedAt={post.created_at}
            votingWindowHours={packInfo?.voting_window_hours ?? 24}
            proposalExpiresAt={post.proposal_expires_at}
            proposalStatus={post.proposal_status}
          />
        </div>

      </div>
    </div>
  );
}
